interface PaginationProps {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

const buildPages = (page: number, totalPages: number): (number | "gap")[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages: (number | "gap")[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);
  if (start > 2) pages.push("gap");
  for (let i = start; i <= end; i += 1) pages.push(i);
  if (end < totalPages - 1) pages.push("gap");
  pages.push(totalPages);
  return pages;
};

export default function Pagination({ page, totalPages, onChange }: PaginationProps) {
  if (totalPages <= 1) return null;
  const pages = buildPages(page, totalPages);

  const baseBtn =
    "flex h-9 min-w-9 cursor-pointer items-center justify-center rounded-md border px-3 text-sm font-500 transition-colors";

  return (
    <nav
      className="mt-10 flex flex-wrap items-center justify-center gap-2"
      aria-label="分页导航"
    >
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        className={`${baseBtn} ${
          page === 1
            ? "cursor-not-allowed border-background-800 text-foreground-600"
            : "border-background-700 text-foreground-200 hover:border-accent-500 hover:text-accent-400"
        }`}
        aria-label="上一页"
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-arrow-left-s-line" aria-hidden="true" />
        </span>
        上一页
      </button>

      {pages.map((item, idx) =>
        item === "gap" ? (
          <span
            key={`gap-${idx}`}
            className="flex h-9 w-9 items-center justify-center text-foreground-500"
          >
            ···
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => onChange(item)}
            aria-current={item === page ? "page" : undefined}
            className={`${baseBtn} ${
              item === page
                ? "border-accent-500 bg-accent-500 text-background-950"
                : "border-background-700 text-foreground-200 hover:border-accent-500 hover:text-accent-400"
            }`}
          >
            {item}
          </button>
        )
      )}

      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        className={`${baseBtn} ${
          page === totalPages
            ? "cursor-not-allowed border-background-800 text-foreground-600"
            : "border-background-700 text-foreground-200 hover:border-accent-500 hover:text-accent-400"
        }`}
        aria-label="下一页"
      >
        下一页
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-arrow-right-s-line" aria-hidden="true" />
        </span>
      </button>
    </nav>
  );
}